import {
  type ParamListBase,
  type TabNavigationState,
  useLinkBuilder,
  useLocale,
  useTheme,
} from "@react-navigation/native";
import { type StyleProp, type ViewStyle } from "react-native";
import { type Route, TabBar, TabBarIndicator, type TabDescriptor } from "react-native-tab-view";
import { DrawerTabBarItem } from "./TabBarItem";
import type { MaterialTopTabBarProps } from "@react-navigation/material-top-tabs";

type MaterialLabelProps = Parameters<NonNullable<TabDescriptor<Route>["label"]>>[0];

export interface DrawerTabBarProps extends MaterialTopTabBarProps {
  /** Called when pointer enters a tab */
  onTabHoverIn?: (route: Route, index: number) => void;
  /** Called when pointer leaves a tab */
  onTabHoverOut?: (route: Route, index: number) => void;
  /** Index of the currently hovered tab (drives hover styling) */
  hoveredIndex?: number | null;
  /** Style applied to the hovered tab item wrapper */
  tabBarHoverStyle?: StyleProp<ViewStyle>;
  /** When true, badges use the OOB absolute positioning. When false (default), badges render inline next to the label. */
  absoluteBadge?: boolean;
}

export function DrawerTabBar({
  state,
  navigation,
  descriptors,
  onTabHoverIn,
  onTabHoverOut,
  hoveredIndex,
  tabBarHoverStyle,
  absoluteBadge = false,
  ...rest
}: DrawerTabBarProps) {
  const { colors } = useTheme();
  const { direction } = useLocale();
  const { buildHref } = useLinkBuilder();

  const focusedOptions = descriptors[state.routes[state.index].key].options;

  const activeColor = focusedOptions.tabBarActiveTintColor ?? colors.text;
  const inactiveColor = focusedOptions.tabBarInactiveTintColor ?? colors.text;

  const tabBarOptions = Object.fromEntries(
    state.routes.map((route) => {
      const { options } = descriptors[route.key];

      const {
        title,
        tabBarLabel,
        tabBarButtonTestID,
        tabBarAccessibilityLabel,
        tabBarBadge,
        tabBarShowIcon,
        tabBarShowLabel,
        tabBarIcon,
        tabBarAllowFontScaling,
        tabBarLabelStyle,
      } = options;

      return [
        route.key,
        {
          href: buildHref(route.name, route.params),
          testID: tabBarButtonTestID,
          accessibilityLabel: tabBarAccessibilityLabel,
          badge: absoluteBadge ? tabBarBadge : undefined,
          icon: tabBarShowIcon === false ? undefined : tabBarIcon,
          label:
            tabBarShowLabel === false
              ? undefined
              : typeof tabBarLabel === "function"
                ? ({ labelText, color }: MaterialLabelProps) =>
                    tabBarLabel({
                      focused: state.routes[state.index].key === route.key,
                      color,
                      children: labelText ?? route.name,
                    })
                : undefined,
          labelAllowFontScaling: tabBarAllowFontScaling,
          labelStyle: tabBarLabelStyle,
          labelText:
            options.tabBarShowLabel === false
              ? undefined
              : typeof tabBarLabel === "string"
                ? tabBarLabel
                : title !== undefined
                  ? title
                  : route.name,
        },
      ];
    }),
  );

  return (
    <TabBar
      {...rest}
      navigationState={state}
      options={tabBarOptions}
      direction={direction}
      scrollEnabled={focusedOptions.tabBarScrollEnabled}
      bounces={focusedOptions.tabBarBounces}
      activeColor={activeColor}
      inactiveColor={inactiveColor}
      pressColor={focusedOptions.tabBarPressColor}
      pressOpacity={focusedOptions.tabBarPressOpacity}
      tabStyle={focusedOptions.tabBarItemStyle}
      indicatorStyle={[{ backgroundColor: colors.primary }, focusedOptions.tabBarIndicatorStyle]}
      gap={focusedOptions.tabBarGap}
      android_ripple={focusedOptions.tabBarAndroidRipple}
      indicatorContainerStyle={focusedOptions.tabBarIndicatorContainerStyle}
      contentContainerStyle={focusedOptions.tabBarContentContainerStyle}
      style={[{ backgroundColor: colors.card }, focusedOptions.tabBarStyle]}
      onTabPress={({ route, preventDefault }) => {
        const event = navigation.emit({
          type: "tabPress",
          target: route.key,
          canPreventDefault: true,
        });

        if (event.defaultPrevented) {
          preventDefault();
        }
      }}
      onTabLongPress={({ route }) =>
        navigation.emit({
          type: "tabLongPress",
          target: route.key,
        })
      }
      renderIndicator={({ navigationState: navState, ...indicatorRest }) => {
        return focusedOptions.tabBarIndicator ? (
          focusedOptions.tabBarIndicator({
            state: navState as TabNavigationState<ParamListBase>,
            ...indicatorRest,
          })
        ) : (
          <TabBarIndicator navigationState={navState} {...indicatorRest} />
        );
      }}
      renderTabBarItem={(itemProps) => {
        const { key, route: itemRoute, ...itemRest } = itemProps;
        const routeIndex = state.routes.findIndex((r) => r.key === itemRoute.key);
        const routeOptions = descriptors[itemRoute.key]?.options;
        const isHovered = hoveredIndex === routeIndex;

        // Resolve inline badge content
        const hasInlineBadge = !absoluteBadge && !!routeOptions?.tabBarBadge;
        const inlineBadgeContent =
          hasInlineBadge && routeOptions?.tabBarBadge
            ? (routeOptions.tabBarBadge as (props: { route: typeof itemRoute }) => React.ReactNode)({
                route: itemRoute,
              })
            : null;

        return (
          <DrawerTabBarItem
            key={key}
            route={itemRoute}
            inlineBadge={inlineBadgeContent}
            innerContainerStyle={isHovered ? tabBarHoverStyle : undefined}
            onInnerPointerEnter={onTabHoverIn ? () => onTabHoverIn(itemRoute, routeIndex) : undefined}
            onInnerPointerLeave={onTabHoverOut ? () => onTabHoverOut(itemRoute, routeIndex) : undefined}
            {...itemRest}
          />
        );
      }}
    />
  );
}
